"use client"

import * as React from "react"
import { Check, ChevronDown, Calendar } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

const DAYS = [
  { id: "sunday", name: "Sunday", short: "Sun" },
  { id: "monday", name: "Monday", short: "Mon" },
  { id: "tuesday", name: "Tuesday", short: "Tue" },
  { id: "wednesday", name: "Wednesday", short: "Wed" },
  { id: "thursday", name: "Thursday", short: "Thu" },
  { id: "friday", name: "Friday", short: "Fri" },
  { id: "saturday", name: "Saturday", short: "Sat" },
]

interface DaySelectorProps {
  selectedDays: string[]
  onSelectionChange: (days: string[]) => void
}

export function DaySelector({ selectedDays, onSelectionChange }: DaySelectorProps) {
  const [open, setOpen] = React.useState(false)

  const allSelected = selectedDays.length === 0 || selectedDays.includes("all")

  const toggleDay = (dayId: string) => {
    if (dayId === "all") {
      onSelectionChange([])
      return
    }

    const newSelection = selectedDays.filter(id => id !== "all")
    if (newSelection.includes(dayId)) {
      onSelectionChange(newSelection.filter(id => id !== dayId))
    } else {
      onSelectionChange([...newSelection, dayId])
    }
  }

  const getDisplayText = () => {
    if (allSelected || selectedDays.length === 0) return "Any Day"
    if (selectedDays.length === 1) {
      const day = DAYS.find(d => d.id === selectedDays[0])
      return day?.name || "1 day"
    }
    if (selectedDays.length <= 3) {
      return selectedDays.map(id => DAYS.find(d => d.id === id)?.short).join(", ")
    }
    return `${selectedDays.length} days`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-background/80 backdrop-blur-sm border-border hover:border-primary/50 hover:bg-background h-12 text-left"
        >
          <span className="flex items-center gap-2 truncate">
            <Calendar className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">{getDisplayText()}</span>
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0 bg-card border-border" align="start">
        <div className="max-h-80 overflow-y-auto">
          {/* All Days Option */}
          <div
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/50 border-b border-border",
              allSelected && "bg-primary/10"
            )}
            onClick={() => toggleDay("all")}
          >
            <div className={cn(
              "h-4 w-4 rounded border flex items-center justify-center shrink-0",
              allSelected ? "bg-primary border-primary" : "border-muted-foreground"
            )}>
              {allSelected && <Check className="h-3 w-3 text-primary-foreground" />}
            </div>
            <span className="font-medium">Any Day</span>
          </div>

          {/* Individual Days */}
          {DAYS.map((day) => {
            const isSelected = selectedDays.includes(day.id)
            return (
              <div
                key={day.id}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/50",
                  isSelected && "bg-primary/10"
                )}
                onClick={() => toggleDay(day.id)}
              >
                <div className={cn(
                  "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                  isSelected ? "bg-primary border-primary" : "border-muted-foreground"
                )}>
                  {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <span>{day.name}</span>
              </div>
            )
          })}
        </div>

        {/* Quick Select Buttons */}
        <div className="border-t border-border p-2 flex gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex-1 text-xs h-8"
            onClick={() => onSelectionChange(["friday", "saturday", "sunday"])}
          >
            Weekends
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex-1 text-xs h-8"
            onClick={() => onSelectionChange(["monday", "tuesday", "wednesday", "thursday", "friday"])}
          >
            Weekdays
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
